import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Icon, { IconName } from './Icon/Icon';

/**
 * Props for the Tooltip component.
 *
 * @property {IconName} [icon] - Optional icon to display within the tooltip.
 * @property {number} [iconSize] - Optional size for the icon.
 * @property {ReactNode} children - Content to be displayed inside the tooltip.
 * @property {string} [className] - Optional CSS class for the tooltip container.
 * @property {string} [iconClassName] - Optional CSS class for the icon element.
 */
type TooltipProps = {
    icon?: IconName;
    iconSize?: number;
    children: ReactNode;
    className?: string;
    iconClassName?: string;
};

/**
 * Tooltip component displays an icon and additional information that can be expanded or collapsed.
 *
 * @remarks
 * - The tooltip shows an icon (default: 'info') and renders its children.
 * - Children can include elements with the `.tooltip-expand` class to toggle the expanded state.
 * - When expanded, elements with the `.tooltip-text` class become visible.
 * - The expand/collapse text is automatically managed for `.tooltip-expand` elements.
 *
 * @param {TooltipProps} props - Props for the Tooltip component.
 * @param {string} [props.icon='info'] - Name of the icon to display.
 * @param {number} [props.iconSize=24] - Size of the icon.
 * @param {React.ReactNode} props.children - Content to display inside the tooltip.
 * @param {string} [props.className=''] - Additional class names for the tooltip container.
 * @param {string} [props.iconClassName=''] - Additional class names for the icon.
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <span className="tooltip-expand" />
 *   <span className="tooltip-text d-none">More information here.</span>
 * </Tooltip>
 * ```
 */
const Tooltip: React.FC<TooltipProps> = ({
    icon = 'info',
    iconSize = 24,
    children,
    className = '',
    iconClassName = '',
}) => {
    const [expanded, setExpanded] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Enhance children: add click handler to .tooltip-expand
    const handleExpandClick = (e: React.MouseEvent) => {
        setExpanded(exp => !exp);
    };

    // Ensure tooltip-text is always closed on initial render - scoped to this component
    useEffect(() => {
        if (!tooltipRef.current) return;
        
        tooltipRef.current.querySelectorAll('.tooltip-text').forEach(el => {
            if (el instanceof HTMLElement) {
                el.classList.add('d-none');
                el.classList.remove('d-block');
            }
        });
        tooltipRef.current.querySelectorAll('.tooltip-expand').forEach(expandEl => {
            if (expandEl instanceof HTMLElement) {
                expandEl.classList.add('cursor-pointer');
                expandEl.innerHTML = 'Expand for more.';
            }
        });
    }, []);

    // Update .tooltip-text and .tooltip-expand elements based on `expanded` state - scoped to this component
    useEffect(() => {
        if (!tooltipRef.current) return;
        
        tooltipRef.current.querySelectorAll('.tooltip-text').forEach(el => {
            if (el instanceof HTMLElement) {
                if (expanded) {
                    el.classList.add('d-block');
                    el.classList.remove('d-none');                    
                } else {
                    el.classList.add('d-none');
                    el.classList.remove('d-block');
                }
            }
        });

        tooltipRef.current.querySelectorAll('.tooltip-expand').forEach(expandEl => {
            if (expandEl instanceof HTMLElement) {
                expandEl.classList.add('cursor-pointer');
                expandEl.innerHTML = expanded ? 'Hide Info' : 'Expand for more.';
            }
        });
    }, [expanded]);

    

    return (
        <div ref={tooltipRef} className={`bg-light-blue py-10px px-15px radius-5px d-flex align-items-start gap-6px ${className}`}>
            {icon && (
                <Icon name={icon} size={iconSize} className={`min-w-24px ${iconClassName}`} />
            )}
            <div
                className="fs-16px text-black"
                onClick={e => {
                    // Only toggle if .tooltip-expand is clicked
                    if ((e.target as HTMLElement).classList.contains('tooltip-expand')) {
                        handleExpandClick(e);
                    }
                }}
            >
                {children}
            </div>
            
        </div>
    );
};

export default Tooltip;